import type { FieldMetadata, FieldProvenance, FieldValue, RecordEvent } from '../../data/client';
import { FieldProvider } from '../../fields/FieldContext';
import { fieldShape } from '../../fields/fieldTypeMeta';
import { FieldInput } from '../../fields/input/FieldInput';
import { cx } from '../../ui';
import { ComputedFieldNote, HumanOwnedNote, WriteMarkerNote } from './FieldStatusNotes';
import { LockReleaseNote } from './LockReleaseNote';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';
import { RecordFieldEditor } from './RecordFieldEditor';
import type { FieldWriteMarker } from './useRecordFieldSave';

/**
 * One field, closed or open.
 *
 * THE ROW IS THE CONTROL. There is no pencil that appears on hover - a phone has
 * no hover, so an affordance that lives there does not exist for half of this
 * team. The button is a full-size layer BEHIND the row content instead of wrapping
 * it: a link, a phone number or a relation inside the value stays clickable
 * (`pointer-events` is handed back to them), and no interactive element ends up
 * nested inside a button.
 */
export interface RecordFieldRowProps {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  /** INV-5: present when a human corrected this field. */
  readonly provenance: FieldProvenance | undefined;
  /**
   * D24: the newest `write_blocked` event on this field, when the loaded timeline holds
   * one. Present means an automated writer was actually refused here, which is the case
   * the release control exists for and the only case it renders in (section 12: it sits
   * beside the refused marker, not under every locked field).
   */
  readonly refusal: RecordEvent | undefined;
  readonly marker: FieldWriteMarker | undefined;
  readonly isEditing: boolean;
  readonly isSaving: boolean;
  readonly onStartEdit: () => void;
  /**
   * Fires on pointerdown, BEFORE any open editor's blur can collapse the layout: the
   * parent notes this row as the one the user pressed, and opens it when the current
   * editor closes (see the edit button below for why this is not simply onStartEdit).
   */
  readonly onPreselect: () => void;
  readonly onCancel: () => void;
  /**
   * Resolves when the write has settled, for the one control that has to wait for it
   * (`FilesInput`, wave 1c). The editor path ignores the result exactly as before.
   */
  readonly onCommit: (next: FieldValue) => Promise<void> | void;
  /**
   * D21: the record is archived, so there is nothing to open. The edit layer is REMOVED
   * rather than disabled - a disabled button is still a tab stop announcing an action that
   * does not exist, and the banner above already carries the only action this state has.
   */
  readonly readOnly?: boolean | undefined;
}

/**
 * A FILES FIELD HAS NO CLOSED STATE (`docs/decisions/file-attachments.md` D18).
 *
 * Every other field on this page is a value you open, type into and Save. An attachment list
 * is not: there is no draft to hold, nothing for Cancel to restore, and an upload has no
 * later moment than its own completion - which is exactly the argument `INSTANT_COMMIT_SHAPES`
 * makes for the same field in a table cell.
 *
 * So the row renders the list LIVE, with Download and Remove per file and one "Add files"
 * button under it, and the full-size edit LAYER is not drawn at all. Drawing it would put an
 * invisible button over the whole row, and every tap meant for Download would open an editor
 * whose Save button has nothing to save.
 */
function isAlwaysOpen(field: FieldMetadata): boolean {
  return fieldShape(field.type) === 'files';
}

export function RecordFieldRow({
  objectId,
  recordId,
  field,
  value,
  provenance,
  refusal,
  marker,
  isEditing,
  isSaving,
  onStartEdit,
  onPreselect,
  onCancel,
  onCommit,
  readOnly = false,
}: RecordFieldRowProps) {
  /**
   * D2: A COMPUTED FIELD IS READ-ONLY WHEREVER IT APPEARS, not only inside the health block.
   *
   * The setting is generic - any field on any object may carry it - so the row rather than
   * one screen is where it has to be honoured. Otherwise a computed field an operator adds
   * next year would offer an editor that the write path refuses, and the refusal would be
   * the first anybody heard of the rule.
   */
  const computed = field.options?.computed === true;
  const locked = readOnly || computed;
  if (isEditing && !locked) {
    return (
      <RecordFieldEditor
        objectId={objectId}
        recordId={recordId}
        field={field}
        value={value}
        humanOwned={provenance !== undefined}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  const alwaysOpen = isAlwaysOpen(field);

  return (
    /*
      P5-2: THE ROW IS THE KIT'S FORMS ROW NOW - label leading, value trailing, at the
      kit's row height inside the group box - and the plain two-column grid this page
      drew until now is gone. `spec/screens/record.md` closed its own HYPOTHESIS on
      exactly this: the alternative had no kit master at all.

      The edit layer is an ABSOLUTE child of the row rather than of a wrapper around it,
      so consecutive rows are real siblings and the group box's hairline between them is
      one CSS rule instead of a divider element. An absolutely positioned grid child
      occupies no grid track, so the two columns are unchanged.
    */
    <div
      className={cx(
        'record-row record-row-editable pointer-events-none relative [&_a]:pointer-events-auto [&_button]:pointer-events-auto',
        /*
          AND A BLOCK TAKES THE WHOLE ROW OF CELLS (wave 3 round 2). In the three-column field
          grid a cell is a third of the group box, which is the right size for a value and the
          wrong one for a list of attachments or a paragraph: a files field renders its list
          live with a Download and a Remove per file, and a multiline text field holds prose.
          Both span the grid; `.record-grid > .record-row-wide` in index.css is the one rule,
          and outside the grid the class does nothing.
        */
        (alwaysOpen || field.options?.multiline === true) && 'record-row-wide',
      )}
    >
      {!locked && !alwaysOpen && (
        <button
          type="button"
          // PRESELECT on pointerdown, open on click. With another field's editor
          // open, that editor commits on blur and collapses, so every row below it
          // moves up BETWEEN mousedown and mouseup - the click lands on the wrong
          // row and only ever closes. The pointerdown records which row the user
          // actually pressed (a ref, no re-render: unmounting this button
          // mid-gesture would blur the editor into an instant self-commit), and
          // the editor's own close hands the ref the stage. Keyboard activation
          // fires no pointerdown and takes the plain click path.
          onPointerDown={(event) => {
            if (event.button === 0) onPreselect();
          }}
          onClick={onStartEdit}
          /**
           * AND IT PAINTS NOTHING ON HOVER (wave 3, R-B4; owner-30: "Wenn ich hier
           * drueber hovere verschwindet einfach wo ich gerade drauf bin").
           *
           * This layer is `absolute` and its siblings - the label and the value - are
           * in flow, so it paints ABOVE them: a background on it covered the row's own
           * text with an opaque wash, and the row a pointer was aiming at was the one
           * row a reader could not read. It was not a tint over the row, it was a lid
           * on it.
           *
           * The fill is gone rather than moved under the text, because Apple's form
           * rows do not change on hover at all: the HIG's list feedback is for
           * SELECTION ("Provide appropriate feedback when people select a list item"),
           * and the kit's Forms row masters ship Default, Selected and Disabled with no
           * hover state to draw.
           * hig: https://developer.apple.com/design/human-interface-guidelines/lists-and-tables
           */
          className="absolute inset-0 rounded-control"
        >
          <span className="sr-only">Edit {field.label}</span>
        </button>
      )}

      <span className="record-row-label">{field.label}</span>
      <div className="record-row-value">
        {/*
          POSITIONED, SO A LINK IN THE VALUE IS THE LINK (BR-RECORD, found by the integration
          gate: "button.absolute.inset-0 intercepts pointer events" over a Website value). The
          edit layer above is absolute and this block was in flow, so the layer painted over it
          and took the click meant for the link. Positioned and later in the document, the value
          paints above the layer; the row keeps `pointer-events: none` on everything but links
          and buttons, so a press anywhere else in the value still reaches the layer.
        */}
        <div className="relative min-w-0">
          {alwaysOpen ? (
            /**
             * The LIVE control, in the read row. `onChange` commits straight through:
             * D18's "an upload commits on completion", and a removal is the same shape -
             * the confirmation the user already answered IS the deliberate step.
             */
            <FieldProvider
              value={{
                objectId,
                recordId,
                field,
                value,
                onChange: onCommit,
                readOnly: locked,
                humanOwned: provenance !== undefined,
              }}
            >
              <FieldInput />
            </FieldProvider>
          ) : (
            <ReadOnlyFieldValue
              objectId={objectId}
              recordId={recordId}
              field={field}
              value={value}
              humanOwned={provenance !== undefined}
            />
          )}
        </div>
        {/**
         * D24 and Arif's question-2 intent, and section 12's word is BESIDE: the
         * standing lock keeps its marker, and where a machine was actually refused the
         * old value, the attempted value and the release control appear next to it.
         *
         * It stays in the VALUE column and not in the status column beside it, because
         * it is a block of prose with a control in it rather than a small accessory,
         * and an `auto` trailing column would size the whole row to its width.
         */}
        {provenance !== undefined && refusal !== undefined && (
          <LockReleaseNote
            objectId={objectId}
            recordId={recordId}
            field={field}
            provenance={provenance}
            value={value}
            refusal={refusal}
          />
        )}
      </div>
      {/*
        THE ROW'S STATUS, AT ITS TRAILING EDGE (P5-8's sibling finding). These controls
        sit ON TOP of the row's edit layer so they stay hoverable, which means wherever
        they sit is a hole in the row's own click target. Under a 167-wide value that
        hole was the row's centre point - the exact place a pointer aiming at the row
        lands - and six browser tests timed out on "subtree intercepts pointer events".
        At the trailing edge they are a Forms row's trailing accessory and intercept
        nothing.
      */}
      <div className="record-row-status">
        {isSaving && <p className="text-2xs text-ink-subtle">Saving</p>}
        {computed && <ComputedFieldNote />}
        {provenance !== undefined && <HumanOwnedNote provenance={provenance} />}
        {marker !== undefined && (
          <WriteMarkerNote objectId={objectId} recordId={recordId} field={field} marker={marker} />
        )}
      </div>
    </div>
  );
}
